import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Environment, useAnimations, useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { VISEMES } from 'wawa-lipsync';
import { lipsyncManager } from '../lipsync';

// Traverses the RPM skinned meshes and lerps a named morph target on all of them.
function makeLerpMorph(scene) {
  return function lerpMorph(name, value, speed = 0.25) {
    scene.traverse((child) => {
      if (child.isSkinnedMesh && child.morphTargetDictionary) {
        const i = child.morphTargetDictionary[name];
        if (i === undefined) return;
        child.morphTargetInfluences[i] = THREE.MathUtils.lerp(
          child.morphTargetInfluences[i],
          value,
          speed
        );
      }
    });
  };
}

function AinaModel({ animation = 'Idle', ...props }) {
  const { scene } = useGLTF('/avatar_fixed.glb');
  const { animations } = useGLTF('/animations.glb');
  const group = useRef();
  const { actions, mixer } = useAnimations(animations, group);
  const [blink, setBlink] = useState(false);
  const currentRef = useRef(null);

  // Crossfade whenever the desired animation changes.
  // Weight < 1 blends the clip toward the rest pose (less head/body sway);
  // timeScale < 1 slows the motion so it feels calm.
  useEffect(() => {
    const CALM = {
      Idle:      { weight: 0.35, speed: 0.55 },
      Laughing:  { weight: 0.55, speed: 0.85 },
      Talking_0: { weight: 0.45, speed: 0.8 },
      Talking_1: { weight: 0.45, speed: 0.8 },
      Talking_2: { weight: 0.55, speed: 0.85 },
    };
    const nextName = actions[animation] ? animation : 'Idle';
    if (currentRef.current === nextName) return;
    const next = actions[nextName];
    if (!next) return;
    const cfg = CALM[nextName] || { weight: 0.5, speed: 0.85 };
    const prevName = currentRef.current;
    const prev = prevName ? actions[prevName] : null;
    const fade = mixer.stats.actions.inUse === 0 ? 0 : 0.5;
    prev?.fadeOut(fade);
    next.reset();
    next.setEffectiveWeight(cfg.weight);
    next.setEffectiveTimeScale(cfg.speed);
    next.fadeIn(fade).play();
    currentRef.current = nextName;
  }, [animation, actions, mixer]);

  // Random blinks
  useEffect(() => {
    let t;
    const loop = () => {
      t = setTimeout(() => {
        setBlink(true);
        setTimeout(() => {
          setBlink(false);
          loop();
        }, 160);
      }, 2000 + Math.random() * 3200);
    };
    loop();
    return () => clearTimeout(t);
  }, []);

  const lerpRef = useRef(null);
  useEffect(() => {
    lerpRef.current = makeLerpMorph(scene);
  }, [scene]);

  useFrame(() => {
    const lerp = lerpRef.current;
    if (!lerp) return;

    lerp('eyeBlinkLeft', blink ? 1 : 0, 0.5);
    lerp('eyeBlinkRight', blink ? 1 : 0, 0.5);

    // Analyse the current TTS audio and pick a viseme
    lipsyncManager.processAudio();
    const active = lipsyncManager.viseme;
    const state = lipsyncManager.state;

    Object.values(VISEMES).forEach((v) => {
      const target = v === active ? 1 : 0;
      const speed = state === 'vowel' ? (v === active ? 0.25 : 0.15) : (v === active ? 0.4 : 0.25);
      lerp(v, target, speed);
    });
  });

  return (
    <group ref={group} {...props} dispose={null}>
      <primitive object={scene} />
    </group>
  );
}

useGLTF.preload('/avatar_fixed.glb');
useGLTF.preload('/animations.glb');

export default function Avatar({ speaking, listening, animation = 'Idle' }) {
  return (
    <div
      className={`avatar-wrap avatar-3d ${listening ? 'is-listening' : ''} ${
        speaking ? 'is-speaking' : ''
      }`}
    >
      <div className="avatar-glow" />
      <div className="avatar-frame3d">
        <Canvas
          shadows
          camera={{ position: [0, 0.05, 1.5], fov: 26 }}
          gl={{ alpha: true, antialias: true, preserveDrawingBuffer: false }}
        >
          {/* No background — canvas is transparent so she blends into the shop scene */}

          {/* Soft high-key portrait lighting */}
          <ambientLight intensity={0.85} />
          <directionalLight
            position={[2, 3, 3]}
            intensity={1.8}
            color="#fff4e6"
            castShadow
            shadow-mapSize={[1024, 1024]}
          />
          <directionalLight position={[-2.5, 1.5, 2]} intensity={1.0} color="#ffffff" />
          <directionalLight position={[0, 2, -2.5]} intensity={0.6} color="#e7ecff" />

          <Environment preset="studio" background={false} />

          <Suspense fallback={null}>
            {/* Model shifted so head + hair + shoulders all fit in a clean face-forward crop */}
            <group position={[0, -1.6, 0]}>
              <AinaModel animation={animation} />
            </group>
          </Suspense>
        </Canvas>
      </div>
    </div>
  );
}
